<!--
  Freeze Inspector — DEV-ONLY badge + modal for a single market.

  Renders a small snowflake icon next to a market when BOTH:
    - window.__DEBUG_FREEZE_UI === true   (opt-in via DevTools; auto-on in dev)
    - market.frozenApplied === true       (the market carries admin-frozen odds)

  Clicking the badge opens a modal that shows:
    - Rule source (platform / manager) and applied timestamp
    - Per-outcome breakdown: raw feed value / frozen value / delta / active
    - A hint reminding the operator that these values are STATIC — they will
      NOT change via feed updates until the admin removes the freeze rule.

  Design mirrors KeyInspector for consistency. Zero cost in production
  (the `v-if` excludes the DOM completely when the debug flag is off).
-->
<template>
  <span v-if="visible" class="freeze-inspector">
    <v-btn icon x-small
           :color="frozenBy === 'platform' ? 'cyan darken-2' : 'purple'"
           class="freeze-inspector-btn"
           @click.stop="dialog = true"
           :title="`Freeze inspector — source: ${frozenBy || 'unknown'}`">
      <v-icon x-small>mdi-snowflake</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" :max-width="600" scrollable>
      <!-- Force LIGHT theme on the whole card. Without this, when the host
           app runs in dark mode all text stays white — and the highlighted
           frozen rows (light-cyan background) become unreadable. `light`
           locks Vuetify's theme scope to the light palette here regardless
           of the app-wide default. Keeps the modal readable in both modes. -->
      <v-card v-if="market" class="freeze-inspector-card" light>
        <v-toolbar dense flat color="cyan darken-2" dark>
          <v-icon left>mdi-snowflake</v-icon>
          <v-toolbar-title class="text-subtitle-2 font-weight-bold">
            Freeze inspector — {{ market.marketName || market.name || market.id }}
            <!-- Show the specifier / baseline for multi-line markets so the
                 operator can tell Over/Under 2.5 apart from 3.5 in the debug
                 modal. On 1X2-style markets `specifier` is undefined and this
                 chip stays hidden. -->
            <v-chip v-if="specifierLabel" small color="cyan lighten-4"
                    text-color="cyan darken-4"
                    class="ml-2 font-weight-bold">
              <v-icon x-small left>mdi-tune</v-icon>
              {{ specifierLabel }}
            </v-chip>
          </v-toolbar-title>
          <v-spacer/>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">

          <!-- STATE ------------------------------------------------------- -->
          <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">
            <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
            Freeze state
          </div>
          <v-simple-table dense class="mb-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="text-caption grey--text">marketId</td>
                  <td class="mono">{{ market.id || market.unique_id }}</td>
                </tr>
                <tr v-if="specifierLabel">
                  <td class="text-caption grey--text">specifier / line</td>
                  <td>
                    <v-chip small color="cyan lighten-4" text-color="cyan darken-4"
                            class="mono font-weight-bold">
                      {{ specifierLabel }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="text-caption grey--text">frozenBy</td>
                  <td>
                    <v-chip small
                            :color="frozenBy === 'platform' ? 'cyan darken-2' : 'purple'"
                            dark>
                      <v-icon x-small left>mdi-snowflake</v-icon>
                      {{ frozenBy || 'unknown' }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="text-caption grey--text">outcomes frozen</td>
                  <td class="text-caption">
                    {{ frozenOutcomesCount }} / {{ (market.outcome || []).length }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- OUTCOMES ---------------------------------------------------- -->
          <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-1 mb-1">
            <v-icon x-small class="mr-1">mdi-format-list-bulleted</v-icon>
            Outcomes
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-caption">Name</th>
                  <th class="text-caption text-right">raw</th>
                  <th class="text-caption text-right">served (frozen)</th>
                  <th class="text-caption text-right">Δ</th>
                  <th class="text-caption text-center">active</th>
                  <th class="text-caption text-center">frozen?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="o in market.outcome || []"
                    :key="o.unique_id || o.outcome_id"
                    :class="{ 'freeze-row-hl': o.frozenApplied }">
                  <td class="text-caption">{{ o.outcome_alias || o.name || o.outcomeName || o.unique_id }}</td>
                  <td class="text-caption text-right mono">{{ formatNum(o.outcome_odd_original) }}</td>
                  <td class="text-caption text-right mono">{{ formatNum(o.outcome_odd_value) }}</td>
                  <td class="text-caption text-right mono" :class="deltaClass(o)">
                    {{ formatDelta(o) }}
                  </td>
                  <td class="text-center">
                    <v-icon x-small :color="o.active ? 'success' : 'grey'">
                      {{ o.active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </td>
                  <td class="text-center">
                    <v-icon v-if="o.frozenApplied" x-small color="cyan darken-2">mdi-snowflake</v-icon>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- HINT ------------------------------------------------------- -->
          <v-alert dense outlined type="info" class="text-caption mt-3 mb-0"
                   icon="mdi-information-outline" color="cyan darken-2">
            <strong>Static values.</strong> These odds are locked at the
            admin-frozen values. Feed updates and KEY math are IGNORED for
            the frozen outcomes until the freeze rule is removed via the
            admin UI (Events → Freeze Odds).
            <span v-if="frozenBy === 'manager'" class="d-block mt-1">
              Source is <strong>MANAGER</strong> — this platform inherits the
              cross-skin freeze; you can override it by creating a PLATFORM
              rule for the same event+market.
            </span>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
/**
 * Fully self-contained: no vuex dependency, no store mutation, no store
 * getters. Reads only the `market` prop and the global `__DEBUG_FREEZE_UI`
 * flag. Kept simple so it can be dropped into any market renderer without
 * side effects — same discipline as KeyInspector.
 */
export default {
    name: 'FreezeInspector',
    props: {
        market: { type: Object, required: true }
    },
    data() {
        return {
            dialog: false,
            // Polling tick to react to `window.__DEBUG_FREEZE_UI` being flipped
            // from DevTools without a full page reload. Cheap — no cost when
            // the flag doesn't change.
            debugTick: 0
        };
    },
    computed: {
        visible() {
            void this.debugTick;
            const flag = typeof window !== 'undefined' && window.__DEBUG_FREEZE_UI === true;
            return flag
                && this.market
                && this.market.frozenApplied === true;
        },
        frozenBy() {
            return this.market?.frozenBy || null;
        },
        frozenOutcomesCount() {
            const arr = this.market?.outcome || [];
            let c = 0;
            for (const o of arr) { if (o?.frozenApplied === true) c++; }
            return c;
        },
        // Specifier / line label for multi-line markets. The payload uses
        // different field names depending on which transformer built it:
        //   - eventTransformer  → market.specifier = baseLine (e.g. "2.5")
        //   - eventDetailBuilder → market.specifier = spec string
        //   - refresh V2         → included in the unique_id "1#2.5"
        // Fall back through them so the chip shows something meaningful on
        // any code path. Returns null on 1X2-style markets (no specifier).
        specifierLabel() {
            const m = this.market || {};
            const raw = m.specifier
                     ?? m.baseLine
                     ?? m.line
                     ?? (typeof m.unique_id === 'string' && m.unique_id.includes('#')
                            ? m.unique_id.split('#')[1]
                            : null);
            if (raw == null || raw === '') return null;
            return String(raw);
        }
    },
    mounted() {
        this._debugPollId = setInterval(() => { this.debugTick++; }, 1000);
    },
    beforeDestroy() {
        if (this._debugPollId) clearInterval(this._debugPollId);
    },
    methods: {
        formatNum(v) {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n.toFixed(2) : '—';
        },
        formatDelta(o) {
            const raw = parseFloat(o?.outcome_odd_original);
            const served = parseFloat(o?.outcome_odd_value);
            if (!Number.isFinite(raw) || !Number.isFinite(served)) return '—';
            const d = served - raw;
            const sign = d > 0 ? '+' : '';
            return `${sign}${d.toFixed(2)}`;
        },
        deltaClass(o) {
            const raw = parseFloat(o?.outcome_odd_original);
            const served = parseFloat(o?.outcome_odd_value);
            if (!Number.isFinite(raw) || !Number.isFinite(served)) return 'grey--text';
            const d = served - raw;
            if (d > 0) return 'green--text';
            if (d < 0) return 'red--text';
            return 'grey--text';
        }
    }
};
</script>

<style scoped>
.freeze-inspector { display: inline-block; }
.freeze-inspector-btn { vertical-align: middle; }
.freeze-inspector-card .mono {
    font-family: 'Roboto Mono', 'Consolas', monospace;
    font-size: var(--font-size-3xl);
}

/* Highlight background for frozen outcome rows. The v-card wrapper carries
   `light` so cell text already defaults to dark — we only need the
   background tint here. */
.freeze-inspector-card ::v-deep .freeze-row-hl {
    background-color: #E0F7FA; /* cyan lighten-5 */
}
</style>
